import '@mui/x-date-pickers/themeAugmentation'
import '@mui/x-date-pickers-pro/themeAugmentation'
import { Components, Theme } from '@mui/material'

export const MuiDatePicker: Components<Omit<Theme, 'components'>>['MuiDatePicker'] = {
  defaultProps: {
    // TODO - new icon
    // slots: { openPickerIcon: FutureCalendarIcon },
    slotProps: {
      openPickerButton: {
        color: 'secondary',
        variant: 'futureTheme.ghost',
        size: 'small',
        sx: {
          // Stop the date picker icon from stretching the text field height
          marginY: -0.5,
        },
      },
    },
  },
}

export const MuiPickersPopper: Components<Omit<Theme, 'components'>>['MuiPickersPopper'] = {
  styleOverrides: {
    paper: ({ theme }) => ({
      marginTop: theme.spacing(1.5),
      borderRadius: theme.shape['futureTheme.radius-lg'],
      boxShadow: theme.futureTheme.shadows.sm,
      borderWidth: theme.futureTheme.strokeWidth.default,
      borderStyle: 'solid',
      borderColor: theme.palette.futureTheme.border.subtle,
      paddingBottom: theme.spacing(4),
      backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
      backgroundImage: 'none', // MUI applies a default background image for Paper
      color: theme.palette.futureTheme.text.default,
    }),
  },
}

export const MuiPickersLayout: Components<Omit<Theme, 'components'>>['MuiPickersLayout'] = {}

const DAY_SIZE_PX = 36
export const MuiDateCalendar: Components<Omit<Theme, 'components'>>['MuiDateCalendar'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // 7 * days
      // 6 * gap between days
      // 2 * padding on month container
      // 304px
      width: `calc(7 * ${DAY_SIZE_PX}px + 6 * ${theme.spacing(0.5)} + 2 * ${theme.spacing(5)})`,
    }),
  },
}

export const MuiPickersCalendarHeader: Components<Omit<Theme, 'components'>>['MuiPickersCalendarHeader'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(5),
      padding: theme.spacing(0, 5),
      minHeight: 28,
      height: 28,
    }),
    switchViewButton: ({ theme }) => ({
      color: theme.palette.futureTheme.fg.muted,
    }),
  },
}
export const MuiPickersArrowSwitcher: Components<Omit<Theme, 'components'>>['MuiPickersArrowSwitcher'] = {
  styleOverrides: {
    button: ({ theme }) => ({
      color: theme.palette.futureTheme.fg.muted,
    }),
  },
}

export const MuiDayCalendar: Components<Omit<Theme, 'components'>>['MuiDayCalendar'] = {
  styleOverrides: {
    header: ({ theme }) => ({
      margin: theme.spacing(0, 5),
      gap: theme.spacing(0.5),
    }),
    weekDayLabel: ({ theme }) => ({
      ...theme.typography['futureTheme.body-regular'],
      color: theme.palette.futureTheme.text.subtle,
      height: DAY_SIZE_PX,
      margin: 0,
    }),
    weekContainer: ({ theme }) => ({
      margin: theme.spacing(0, 5),
      gap: theme.spacing(0.5),
    }),
    monthContainer: ({ theme }) => ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    }),
    slideTransition: {
      minHeight: 230,
    },
  },
}

export const MuiPickersYear: Components<Omit<Theme, 'components'>>['MuiPickersYear'] = {
  styleOverrides: {
    yearButton: ({ theme }) => ({
      borderRadius: theme.shape['futureTheme.radius-lg'],
      ...theme.typography['futureTheme.body-regular'],

      '&:not(.Mui-disabled):hover': {
        backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
      },

      '&.Mui-selected': {
        backgroundColor: theme.palette.futureTheme.bg.brand.default,
        color: theme.palette.futureTheme.text.brand.onBrand,
        // MUI default sets an override on focused
        '&:focus': {
          backgroundColor: theme.palette.futureTheme.bg.brand.default,
        },
        '&:hover': {
          backgroundColor: theme.palette.futureTheme.bg.brand.hover,
        },
      },
    }),
  },
}

export const MuiPickersDay: Components<Omit<Theme, 'components'>>['MuiPickersDay'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape['futureTheme.radius-lg'],

      ...theme.typography['futureTheme.body-regular'],
      color: theme.palette.futureTheme.text.default,

      '&:not(.Mui-disabled):hover': {
        backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
      },

      '&.MuiPickersDay-today:not(.Mui-selected)': {
        borderStyle: 'solid',
        borderWidth: theme.futureTheme.strokeWidth.strong,
        backgroundColor: theme.palette.futureTheme.bg.invisible,
        borderColor: theme.palette.futureTheme.border.strong,
        '&:not(.Mui-disabled):hover': {
          backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
        },
      },
      '&.Mui-selected': {
        border: 'none',
        backgroundColor: theme.palette.futureTheme.bg.brand.default,
        color: theme.palette.futureTheme.text.brand.onBrand,
        // MUI default sets an override on focused
        '&:focus': {
          backgroundColor: theme.palette.futureTheme.bg.brand.default,
        },
        '&:hover': {
          backgroundColor: theme.palette.futureTheme.bg.brand.hover,
        },
      },
    }),
    dayWithMargin: ({ theme }) => ({
      margin: theme.spacing(0),
    }),
  },
}

export const MuiDateRangePickerDay: Components<Omit<Theme, 'components'>>['MuiDateRangePickerDay'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      '&:last-of-type': {
        // End of row - selected highlight
        borderTopRightRadius: theme.shape['futureTheme.radius-lg'],
        borderBottomRightRadius: theme.shape['futureTheme.radius-lg'],
        // End of row - preview highlight
        '& .MuiDateRangePickerDay-rangeIntervalPreview': {
          borderTopRightRadius: theme.shape['futureTheme.radius-lg'],
          borderBottomRightRadius: theme.shape['futureTheme.radius-lg'],
        },
      },
      '&:first-of-type': {
        // Start of row - selected highlight
        borderTopLeftRadius: theme.shape['futureTheme.radius-lg'],
        borderBottomLeftRadius: theme.shape['futureTheme.radius-lg'],
        '& .MuiDateRangePickerDay-rangeIntervalPreview': {
          // Start of row - preview highlight
          borderTopLeftRadius: theme.shape['futureTheme.radius-lg'],
          borderBottomLeftRadius: theme.shape['futureTheme.radius-lg'],
        },
      },
    }),
    rangeIntervalDayPreviewStart: ({ theme }) => ({
      // Start of range - preview highlight
      borderTopLeftRadius: theme.shape['futureTheme.radius-lg'],
      borderBottomLeftRadius: theme.shape['futureTheme.radius-lg'],
    }),
    rangeIntervalDayPreviewEnd: ({ theme }) => ({
      // End of range - preview highlight
      borderTopRightRadius: theme.shape['futureTheme.radius-lg'],
      borderBottomRightRadius: theme.shape['futureTheme.radius-lg'],
    }),
  },
}
export const MuiDateRangeCalendar: Components<Omit<Theme, 'components'>>['MuiDateRangeCalendar'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // Use item spacing not gap to avoid issues with continuous selection background on range
      // Not possible on normal day picker as it doesn't nest days in the same way
      '& .MuiDayCalendar-weekContainer': {
        gap: 0,
      },
      '& .MuiDayCalendar-header': {
        gap: theme.spacing(1),
      },
    }),
  },
}
